import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from "@angular/router";
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { DatePipe } from "@angular/common";
import { Subscription, BehaviorSubject, of } from "rxjs";
import { catchError } from "rxjs/operators";
//import { GridOptions, ColDef } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { CanDeactivateComponent } from "../../../../common/classes/can-deactivate.component";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { AuthService } from "../../../../services/auth.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { DocumentManagementService, IUploadFile } from "../../../../common/services/document-management.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { WellService } from "../../../../common/services/well.service";
import { UploadViewFilesComponent } from "../../../../common/components/fileUpload/upload-view-files.component";
import { ParseFileResponse, ParserError } from "../../../../builder/components/state/controls/config/data/file-parser-config/parser.model";
import { BulkWellStatusChange, BulkWellStatusChangeDetails, BulkWellStatusChangeStatus, BulkWellStatusChangeRequest, BulkWellStatusChangeFile } from "../../../models/bulk-well-status-change.model";


@Component({
    selector: 'app-submit-bulk-well-update',
    templateUrl: './submit-bulk-well-update.component.html',
    styleUrls: ['./submit-bulk-well-update.component.scss'],
    standalone: false
})
export class SubmitBulkWellUpdateComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    confirmPartialUpload: Dialog = new Dialog("Some of the rows in the file contain errors.  Rows containing errors will not be included in the submitted request.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.confirmSubmit(); }), new DialogButton("No", false, () => { return; })]);


    @ViewChild("downloadLink") public downloadLink: ElementRef;

    @ViewChild(UploadViewFilesComponent) filesComponent: UploadViewFilesComponent;

    get dataColumns(): ColDef[] {
        return [
            {
                headerClass: "wrapped",
                headerName: "API #",
                headerTooltip: "API #",
                field: "apiNumber",
                tooltipField: "apiNumber",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 125,
                maxWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Name",
                headerTooltip: "Well Name",
                field: "wellName",
                tooltipField: "wellName",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 110,
                maxWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Well Number",
                headerTooltip: "Well Number",
                field: "wellNumber",
                tooltipField: "wellNumber",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 110,
                maxWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "From Well Status",
                headerTooltip: "From Well Status",
                field: "fromWellStatus",
                tooltipField: "fromWellStatus",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 110,
                maxWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "To Disposition",
                headerTooltip: "To Disposition",
                field: "toDisposition",
                tooltipField: "toDisposition",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 125,
                maxWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "To Status Reason",
                headerTooltip: "To Status Reason",
                field: "toStatusReason",
                tooltipField: "toStatusReason",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 110,
                maxWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "To Well Type Description",
                headerTooltip: "To Well Type Description",
                field: "toTypeDescription",
                tooltipField: "toTypeDescription",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 150,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Map Symbol",
                headerTooltip: "Map Symbol",
                field: "mapSymbol",
                tooltipField: "mapSymbol",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 110,
                maxWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "New Status Date",
                headerTooltip: "New Status Date",
                field: "toStatusDate",
                tooltipValueGetter: this.formatDate("toStatusDate", true),
                valueFormatter: this.formatDate("toStatusDate", true),
                suppressMovable: true,
                filter: "agDateColumnFilter",
                filterParams: this._formatter.defaultDateFilterParams,
                minWidth: 110,
                maxWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Date Uploaded",
                headerTooltip: "Date Uploaded",
                field: "dateUploaded",                
                tooltipValueGetter: this.formatDate("dateUploaded", true),
                valueFormatter: this.formatDate("dateUploaded", true),
                suppressMovable: true,
                filter: "agDateColumnFilter",
                filterParams: this._formatter.defaultDateFilterParams,
                minWidth: 125,
                maxWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Notes",
                headerTooltip: "Notes",
                field: "notes",
                tooltipField: "notes",
                suppressMovable: true,
                filter: "agTextColumnFilter",
                filterParams: this._formatter.defaultTextFilterParams,
                minWidth: 300
            } as ColDef
        ] as ColDef[];
    }

    get errorData(): ParserError[] {
        return this.form.get('errorData').value;
    }

    set errorData(value: ParserError[]) {
        this.form.get('errorData').setValue(value);
    }

    get errorGridColumns(): ColDef[] {
        return [
            {
                headerName: "Line",
                headerTooltip: "Line",
                field: "line",
                tooltipField: "line",
                suppressMovable: true,
                maxWidth: 125
            } as ColDef,
            {
                headerName: "Column",
                headerTooltip: "Column",
                field: "column",
                tooltipField: "column",
                suppressMovable: true,
                maxWidth: 125
            } as ColDef,
            {
                headerName: "Error",
                headerTooltip: "Error",
                field: "error",
                tooltipField: "error",
                suppressMovable: true
            } as ColDef
        ];
    }

    errorGridOptions: GridOptions; 
	errorGridApi?: GridApi;
    form: FormGroup = new FormGroup({});

    get hasFileListing(): boolean {
        return this.uploadedFiles != null && this.uploadedFiles.length > 0;
    }

    get isParsing(): boolean {
        return this.parsingInProgress.value;
    }


    get parsedData(): BulkWellStatusChangeRequest[] {
        return this.form.get('parsedData').value;
    }

    set parsedData(value: BulkWellStatusChangeRequest[]) {
        this.form.get('parsedData').setValue(value);
    }

    parsedGridOptions: GridOptions;
	parsedGridApi?: GridApi;
	private parsingInProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	statusBar = {
		statusPanels: [
			{
				statusPanel: 'agTotalAndFilteredRowCountComponent',
				align: 'right',
			}
		]
	};

    private subheader: SubHeader = new SubHeader({ title: "Bulk Well Status Update", buttons: [{ name: "Download Sample File", method: () => this.getSample() }] });

    subscription: Subscription = new Subscription();

    successDialog: Dialog = new Dialog("Request ID [id] has been submitted for approval.", [new DialogButton("OK", true, () => { this.home(); })]);

    get uploadedFiles(): IUploadFile[] {
        if (this.form == null) return [];
        let fileCtrl = this.form.get('files');
        if (fileCtrl == null) return [];
        return fileCtrl.value;
    }

    

  
    constructor(dialog: DynamicDialogService, auth: AuthService, private subheaderSvc: SubHeaderService, private builder: FormBuilder, private loader: LoaderService, private docService: DocumentManagementService,
        private _formatter: GridFormatterService, private wellService: WellService, public router: Router, private datePipe: DatePipe) {
        super(dialog, auth);
    }

    ngOnInit() {        
        this.subheaderSvc.updateSubHeader(this.subheader);
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])
        this.allowDeactivate.next(true);
        this.setupGrids();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    private buildForm(editorForm: FormGroup): FormGroup {
        return this.builder.group({
            files: [[], []],
            errorData: [[], []],
            parsedData: [[], [Validators.required]],
            reason: editorForm
        });
    }

    cancel(): void {
        this.form.reset();
    }

    confirmSubmit(): void {
        let request: BulkWellStatusChange = {
            requestId: "",
            status: BulkWellStatusChangeStatus.Pending,
            dateRequested: new Date(),
            requestedById: this.auth.User.id,
            requestedByName: this.auth.User.displayName,
            reasonForRequest: this.form.get('reason').get('content').value,
            bulkWellStatusChangeDetails: [],
            bulkWellStatusChangeFiles: []
        } as BulkWellStatusChange;
        this.parsedData.forEach(p => {
            let detail: BulkWellStatusChangeDetails = {
                requestId: "",
                apiNumber: p.apiNumber,
                wellName: p.wellName,
                wellNumber: p.wellNumber,
                fromWellStatus: p.fromWellStatus,
                toDisposition: p.toDisposition,
                toStatusReason: p.toStatusReason,
                toStatusDate: p.toStatusDate,
                toTypeDescription: p.toTypeDescription,
                mapSymbol: p.mapSymbol,
                dateUploaded: p.dateUploaded,
                notes: p.notes
            } as BulkWellStatusChangeDetails;
            request.bulkWellStatusChangeDetails.push(detail);
        });
        this.uploadedFiles.forEach(f => {
            let file: BulkWellStatusChangeFile = {
                requestId: "",
                blobUri: f.blobUri,
                fileName: f.fileName,
                type: f.type
            } as BulkWellStatusChangeFile;
            const fileParts = f.fileName.split(".");
            if (fileParts.length > 0) {
                file.extension = fileParts[fileParts.length - 1];
            }
            request.bulkWellStatusChangeFiles.push(file);
        });
        this.loader.toggleLoader(true);
        let save$ = this.wellService.saveBulkWellStatusChange(request).pipe(
            catchError((error) => {
                this.handleError(error);
                return of(null);
            }))
            .subscribe(
                (data: BulkWellStatusChange) => {
                    this.loader.toggleLoader(false);
                    if (data != null) {
                        this.successDialog.message = this.successDialog.message.replace('[id]', data.requestId);
                        this.successDialog.toggleDialog(true);
                        this.dialogService.updateDialog(this.successDialog);
                    }
                },
                (error) => {
                    this.loader.toggleLoader(false);
                    this.handleError(error);
                }
            );
        this.subscription.add(save$);
    }


    deleteFile(file: IUploadFile): void {
        let index = this.uploadedFiles.findIndex(f => f.rawFile === file.rawFile);
        if (index !== -1) {
            this.uploadedFiles.splice(index, 1);
            this.filesComponent.removeFile(file);
            this.parsedData = [];
            this.errorData = [];
        }

    }

    editorBuilt(editorForm: FormGroup): void {
        this.form = this.buildForm(editorForm);
    }

    formatDate(field: string, formatted: boolean): any {
        return (obj) => {
            let date: Date = null;
            if (obj != null && obj.data != null && obj.data[field] != null) {
                date = <Date>obj.data[field];
            }
            if (formatted) {
                if (date != null) {
                    return this.datePipe.transform(date, "MM/dd/yyyy", '+0000');
                }
                return "N/A";
            }
            return date;
        }
    }

    getSample(): void {
        this.loader.toggleLoader(true);
        const sample$ = this.docService.getSampleBulkWellStatusChangeFile().pipe()
            .subscribe(
                (data: any) => {
                    this.loader.toggleLoader(false);
                    const fileName = "bulk-status-sample.xlsx";
                    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                    const blob = new Blob([data], { type: fileType });
					const blobUrl = window.URL.createObjectURL(blob);
					const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
					anchor.href = blobUrl;
					anchor.download = fileName;
					anchor.click();

                },
                (error) => {
                    this.handleError(error);
                }
        );
        this.subscription.add(sample$);
    }

    handleError(error: any): void {
        this.loader.toggleLoader(false);
        console.error(error);
        this.dialogService.showError();
    }

    home(): void {
        this.router.navigate(['/home']);
    }

    parseFile(file: IUploadFile): void {
        if (file != null) {
            this.uploadedFiles.push(file);
            this.parsingInProgress.next(true);
            let obs$ = this.docService.parseBulkWellStatusChangeRequest(file);
            const parse$ = obs$.pipe(
                catchError((error) => {
                    this.handleError(error);
                    return of(null);
                })
            ).subscribe(
                (data: ParseFileResponse) => {
                    if (data != null) {
                        this.parsedData = data.data;
                        this.errorData = data.errors;
                    }
                    this.parsingInProgress.next(false);                   
                },
                (error) => {
                    this.parsingInProgress.next(false);
                    this.handleError(error);
                }
            );
            this.subscription.add(parse$);
        }
    }

    setupGrids(): void {
        this.errorGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.errorGridColumns, false, true, true, true);
        this.parsedGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.dataColumns, false, true, true, true);
        this.parsedGridOptions.headerHeight = 50;
		this.parsedGridOptions.pagination = false;
		this.parsedGridOptions.onGridReady = (params) => {
			this.parsedGridApi = params.api;
		};
		this.parsedGridOptions.onFirstDataRendered = () => { 
            if (this.parsedGridApi) {
                this.parsedGridApi.autoSizeAllColumns();
            }
         };
		this.errorGridOptions.pagination = false;
		this.errorGridOptions.onGridReady = (params) => {
			this.errorGridApi = params.api;
		};
        this.errorGridOptions.onFirstDataRendered = () => { 
            if (this.errorGridApi) {
                this.errorGridApi.autoSizeAllColumns();
            }
         }
    }

    submit(): void {
        if (this.errorData != null && this.errorData.length > 0) {
            this.confirmPartialUpload.toggleDialog(true);
            this.dialogService.updateDialog(this.confirmPartialUpload);
        } else {
            this.confirmSubmit();
        }
    }
}
